import React, { useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Phase 302 — Allocate Opening-Balance Credit dialog.
 *
 * Opens from the Opening Balance card when the apartment has a
 * NEGATIVE opening balance with remaining credit. The user picks one
 * or more currently unpaid (or partially paid) invoices and how much
 * of the credit to apply against each.
 *
 * Server endpoint: POST /apartments/{id}/allocate-credit
 *   { invoice_selections: [{ invoice_id, amount }, ...] }
 * The server caps every line at:
 *   min(requested, invoice remaining, credit remaining)
 * so no over-allocation is ever possible.
 *
 * Single source of truth: same backend, same MongoDB. The audit row
 * inserted server-side appears in the Payments tab + Statement.
 */
export default function AllocateCreditDialog({
  open,
  onOpenChange,
  apartment,
  apartmentId,
  unpaidInvoices,
  setUnpaidInvoices,
  amounts,
  setAmounts,
  loading,
  setLoading,
  onAllocated,
  t,
}) {
  const ob = parseFloat(apartment?.opening_balance) || 0;
  const obPaid = parseFloat(apartment?.opening_balance_paid) || 0;
  const creditRemaining = useMemo(
    () => (ob < 0 ? Math.max(0, Math.abs(ob) - obPaid) : 0),
    [ob, obPaid],
  );

  // Load the apartment's currently-unpaid invoices when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/apartments/${apartmentId}/payable-items`);
        if (cancelled) return;
        const items = (res.data || []).filter(i => !i.is_opening_balance && (i.remaining || 0) > 0.005);
        setUnpaidInvoices(items);
        // Pre-fill: cap each at min(remaining, available credit) auto.
        const initial = {};
        let left = creditRemaining;
        for (const inv of items) {
          const give = Math.max(0, Math.min(inv.remaining, left));
          initial[inv.invoice_id] = give > 0 ? give.toFixed(2) : '';
          left = Math.max(0, left - give);
        }
        setAmounts(initial);
      } catch (e) {
        toast.error('Failed to load unpaid invoices.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apartmentId]);

  const totalAllocating = useMemo(
    () => Object.values(amounts || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [amounts],
  );
  const remainingAfter = Math.max(0, creditRemaining - totalAllocating);
  const overAllocated = totalAllocating > creditRemaining + 0.005;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (overAllocated) {
      toast.error('Allocation exceeds available credit.');
      return;
    }
    const selections = unpaidInvoices
      .map(inv => ({
        invoice_id: inv.invoice_id,
        amount: parseFloat(amounts[inv.invoice_id]) || 0,
      }))
      .filter(s => s.amount > 0);
    if (selections.length === 0) {
      toast.error('Pick at least one invoice and an amount.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/apartments/${apartmentId}/allocate-credit`,
        { invoice_selections: selections },
      );
      if (onAllocated) await onAllocated();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Allocation failed.';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-none" data-testid="allocate-credit-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
            {t?.('allocateCredit') || 'Allocate Credit'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm border border-zinc-200 bg-zinc-50 px-3 py-2 font-mono" data-testid="alloc-credit-summary">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Available Credit</p>
              <p className="text-base font-semibold text-zinc-800">&euro;{creditRemaining.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Allocating</p>
              <p className={`text-base font-semibold ${overAllocated ? 'text-red-600' : 'text-zinc-800'}`}>
                &euro;{totalAllocating.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Remaining After</p>
              <p className="text-base font-semibold text-zinc-800">&euro;{remainingAfter.toFixed(2)}</p>
            </div>
          </div>

          {unpaidInvoices.length === 0 ? (
            <div className="text-sm text-zinc-500 italic py-6 text-center">
              No unpaid invoices to allocate against.
            </div>
          ) : (
            <div className="border border-zinc-200 max-h-[50vh] overflow-y-auto" data-testid="alloc-credit-rows">
              <table className="w-full text-sm">
                <thead className="bg-zinc-100 text-[11px] uppercase tracking-wider text-zinc-600">
                  <tr>
                    <th className="text-left px-3 py-2">Invoice</th>
                    <th className="text-left px-3 py-2">Period</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-right px-3 py-2">Remaining</th>
                    <th className="text-right px-3 py-2 w-[140px]">Allocate</th>
                  </tr>
                </thead>
                <tbody>
                  {unpaidInvoices.map(inv => (
                    <tr key={inv.invoice_id} className="border-t border-zinc-200">
                      <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number || '—'}</td>
                      <td className="px-3 py-2 text-zinc-500 text-xs">{inv.period || '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">&euro;{Number(inv.amount).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-mono text-red-600">&euro;{Number(inv.remaining).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max={Math.min(inv.remaining, creditRemaining)}
                          value={amounts[inv.invoice_id] ?? ''}
                          onChange={e => setAmounts({ ...amounts, [inv.invoice_id]: e.target.value })}
                          placeholder="0.00"
                          className="h-8 text-right font-mono rounded-none"
                          data-testid={`alloc-credit-input-${inv.invoice_id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[11px] text-zinc-400 leading-snug">
            Each line is capped at min(invoice remaining, available credit) on the server. The
            credit allocation is recorded as a payment with method "opening_credit" so the audit
            trail is preserved. No money moves and fund balances are not affected.
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-none"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t?.('cancel') || 'Cancel'}
            </Button>
            <Button
              type="submit"
              className="bg-zinc-950 hover:bg-zinc-800 rounded-none"
              disabled={loading || overAllocated || totalAllocating <= 0}
              data-testid="alloc-credit-submit"
            >
              {loading ? 'Allocating…' : (t?.('allocate') || 'Allocate')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
